import { authenticate, login, isUnitTestActive } from '../lib/auth';
import { useXProps } from '../hooks/useXProps';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageNotFound from '../components/templates/PageNotFound';
import TOKEN from '../lib/token';

const withAuth = (
  Component: (props: Record<string, unknown>) => JSX.Element,
) => {
  const ComponentWithAuth = (props: Record<string, unknown>) => {
    const { tenant, initialRoute, canViewLocationsOnboardingFiles } =
      useXProps();
    const navigate = useNavigate();
    const url = new URL(window.location.href);
    // Try to prevent duplicate requests to the auth server
    const loginRequested = useRef(false);
    const authenticated: React.MutableRefObject<boolean | null> = useRef(null);

    const xprops = {
      tenant: window.sessionStorage.getItem('tenant') || tenant,
      initialRoute: window.sessionStorage.getItem('initialRoute'),
    };

    const launch = (tenant: string) => {
      try {
        return <Component {...{ ...props, tenant }} />;
      } catch (e) {
        console.error('Error launching component:', e);
        return <PageNotFound />;
      }
    };

    useEffect(() => {
      // Saves xprops data to session storage for availability
      if (tenant && tenant.trim().length > 0) {
        window.sessionStorage.setItem('tenant', tenant);
        if (initialRoute)
          window.sessionStorage.setItem('initialRoute', initialRoute);
        if (canViewLocationsOnboardingFiles)
          window.sessionStorage.setItem(
            'canViewLocationsOnboardingFiles',
            `${canViewLocationsOnboardingFiles}`,
          );
      }
    });

    useEffect(() => {
      // Checks the auth state on each render
      if (xprops.tenant) {
        if (isUnitTestActive(tenant)) {
          // Bypass auth checks if we are running tests or don't have a tenant
          authenticated.current = true;
          navigate(xprops.initialRoute || '');
          return;
        } else {
          const token = TOKEN.get(); // If we have an auth token, navigate to initial route
          if (token) {
            authenticated.current = true;
            navigate(xprops.initialRoute || '');
          } else if (url.href.includes('code=')) {
            // If there's an auth code, complete the flow
            const code: string | null = url.searchParams.get('code');
            if (code) {
              if (!loginRequested.current) {
                loginRequested.current = true;
                const access_token = authenticate(xprops.tenant, url, code);
                if (access_token !== null) {
                  authenticated.current = true;
                  navigate(xprops.initialRoute || '');
                }
              }
            }
          } else {
            // If we don't have a token or auth code, initiate login
            try {
              if (!loginRequested.current) {
                loginRequested.current = true;
                login(xprops.tenant);
              }
            } catch (e) {
              console.error('Error with login:', e);
            }
          }
        }
      }
    }, [tenant]);

    if (authenticated.current == true) {
      return launch(xprops.tenant);
    }
    return null;
  };

  return ComponentWithAuth;
};

export default withAuth;
